import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * AuthCallback — handles the Emergent Google OAuth return.
 *
 * Emergent redirects to `<origin>/auth/callback#session_id=...` after the
 * user completes Google sign-in.  We synchronously pluck the fragment,
 * POST it to our backend which exchanges it for a SMART JWT + user
 * record, then bounce the user into their dashboard based on role.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
    const navigate = useNavigate();
    const location = useLocation();
    const { setUser, saveToken } = useAuth();
    const processed = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (processed.current) return;
        processed.current = true;

        const hash = location.hash || window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        if (!match) {
            setError("No session fragment found");
            return;
        }
        const sessionId = decodeURIComponent(match[1]);
        // Clean the hash from the URL bar as soon as we've read it.
        try { window.history.replaceState(null, "", window.location.pathname + window.location.search); }
        catch { /* noop */ }

        // Pull optional referral + intended role from localStorage (set by AuthPage before redirect).
        let intent = {};
        try { intent = JSON.parse(localStorage.getItem("smart_google_intent") || "{}"); }
        catch { intent = {}; }
        try { localStorage.removeItem("smart_google_intent"); } catch { /* noop */ }

        api.post("/auth/google/session", {
            session_id: sessionId,
            role: intent.role || "customer",
            referral_code: intent.referral_code || null,
        }).then(({ data }) => {
            if (data.token) saveToken?.(data.token);
            if (data.user) {
                setUser(data.user);
                // Also identify this visitor session so re-engagement crons work.
                try { window.__smartIdentifyVisitor?.({ email: data.user.email, name: data.user.name }); }
                catch { /* noop */ }
                toast.success(`Welcome, ${data.user.name?.split(" ")[0] || "friend"}!`);
                const next = intent.next && intent.next.startsWith("/") ? intent.next : null;
                if (next) navigate(next, { replace: true });
                else if (data.user.role === "admin") navigate("/admin", { replace: true });
                else if (data.user.role === "provider") navigate("/provider", { replace: true });
                else navigate("/my-bookings", { replace: true });
            }
        }).catch((e) => {
            const msg = e.response?.data?.detail || e.message || "Sign-in failed";
            setError(msg);
            toast.error(msg);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center bg-background" data-testid="google-auth-callback">
            <div className="text-center">
                {error ? (
                    <>
                        <div className="overline mb-4 text-red-400">Sign-in error</div>
                        <p className="text-muted-foreground max-w-md mx-auto">{error}</p>
                        <button
                            onClick={() => navigate("/auth?mode=login")}
                            className="mt-6 text-xs uppercase tracking-[0.2em] gold-text border-b border-amber-700/40 pb-1"
                            data-testid="callback-back-btn"
                        >
                            Back to sign in
                        </button>
                    </>
                ) : (
                    <>
                        <Loader2 size={44} strokeWidth={1} className="gold-text mx-auto animate-spin mb-6" />
                        <div className="overline mb-2">Signing you in</div>
                        <p className="text-muted-foreground">Almost there…</p>
                    </>
                )}
            </div>
        </div>
    );
}
